import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Info,
  ShieldCheck,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ModerationDashboardModuleDTO, ModerationMetricDTO } from '../dashboardTypes';
import { DashboardBusy, NotReadyBadge, moderationReadyState } from './DashboardNotReady';
import {
  MiniBarSparkline,
  MiniSparkline,
  formatModCount,
  priorFromDelta,
} from './dashboardChartPrimitives';

type ChartKind = 'bars' | 'area';

const ROW_META: Record<
  string,
  {
    icon: LucideIcon;
    color: string;
    chart: ChartKind;
    upIsGood: boolean;
    format: (v: number) => string;
  }
> = {
  REPORT_RATE: {
    icon: FileText,
    color: '#a78bfa',
    chart: 'bars',
    upIsGood: false,
    format: (v) => `${formatModCount(v)}/1k`,
  },
  CONFIRMED_VIOLATION_RATE: {
    icon: AlertTriangle,
    color: '#60a5fa',
    chart: 'bars',
    upIsGood: false,
    format: (v) => `${Number(v).toFixed(1)}%`,
  },
  HUMAN_REVIEW_RATE: {
    icon: User,
    color: '#fb923c',
    chart: 'bars',
    upIsGood: false,
    format: (v) => `${Number(v).toFixed(1)}%`,
  },
  AVG_TIME_TO_DECISION: {
    icon: Clock,
    color: '#818cf8',
    chart: 'area',
    upIsGood: false,
    format: (v) => `${formatModCount(v)}m`,
  },
  AVG_COOP_DEEP_SCAN_MS: {
    icon: Clock,
    color: '#38bdf8',
    chart: 'area',
    upIsGood: false,
    format: (v) => (v < 1000 ? `${formatModCount(Math.round(v))}ms` : `${(v / 1000).toFixed(1)}s`),
  },
  PROCESSING_SUCCESS_RATE: {
    icon: CheckCircle2,
    color: '#34d399',
    chart: 'area',
    upIsGood: true,
    format: (v) => `${Number(v).toFixed(1)}%`,
  },
};

function metricValue(m: ModerationMetricDTO | undefined): number | null {
  if (m?.value == null) return null;
  const n = typeof m.value === 'number' ? m.value : Number(m.value);
  return Number.isFinite(n) ? n : null;
}

function sparklineValues(m: ModerationMetricDTO): number[] {
  const raw = m.sparkline;
  if (!raw?.length) return [];
  return raw.map((v) => Number(v)).filter((n) => Number.isFinite(n));
}

function healthBannerFromSystemHealth(
  systemHealth: ModerationMetricDTO | undefined,
  errors: ModerationMetricDTO | undefined,
): { tone: 'ok' | 'warn' | 'bad'; title: string; detail: string } {
  const healthVal = metricValue(systemHealth);
  const errVal = metricValue(errors) ?? 0;
  const pctLabel = healthVal != null ? `${formatModCount(healthVal)}%` : null;

  if (errVal >= 10 || (healthVal != null && healthVal < 50)) {
    return {
      tone: 'bad',
      title: pctLabel ? `${pctLabel} Critical` : 'Critical',
      detail:
        errVal > 0
          ? `${formatModCount(errVal)} pipeline errors — pipeline needs attention.`
          : 'Moderation system health is below threshold.',
    };
  }
  if (errVal > 0 || (healthVal != null && healthVal < 100)) {
    return {
      tone: 'warn',
      title: pctLabel ? `${pctLabel} Warning` : 'Warning',
      detail:
        errVal > 0
          ? `${formatModCount(errVal)} pipeline errors — monitor closely.`
          : 'Some health signals need attention.',
    };
  }
  return {
    tone: 'ok',
    title: pctLabel ? `${pctLabel} Healthy` : 'Healthy',
    detail: 'Moderation pipeline operating normally',
  };
}

function trendIsFavorable(delta: number, upIsGood: boolean): boolean {
  if (delta === 0) return true;
  return upIsGood ? delta > 0 : delta < 0;
}

/** Figma: avg-time delta as minutes changed, not percent. */
function formatTrend(
  key: string,
  value: number | null,
  delta: number | null,
  vsLabel: string,
): string | null {
  if (delta == null) return null;
  if (key === 'AVG_TIME_TO_DECISION' && value != null) {
    const prior = priorFromDelta(value, delta);
    if (prior != null) {
      const mins = Math.round(value - prior);
      if (mins === 0) return `→ 0m vs ${vsLabel}`;
      return `${mins > 0 ? '↑' : '↓'} ${Math.abs(mins)}m vs ${vsLabel}`;
    }
  }
  return `${delta >= 0 ? '↑' : '↓'} ${Math.abs(delta).toFixed(1)}% vs ${vsLabel}`;
}

/** Figma Platform Safety Health: banner + icon / value / sparkline / trend rows. */
export function PlatformSafetyHealthCard({
  module,
  systemHealth,
  isLoading,
  className,
  comparisonLabel,
  showPriorComparison = true,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  systemHealth?: ModerationMetricDTO | undefined;
  isLoading?: boolean;
  className?: string;
  /** Prior window label (e.g. "May 13 - May 19"). Omit under All the time (TM-3608). */
  comparisonLabel?: string;
  showPriorComparison?: boolean;
}) {
  const state = moderationReadyState(module?.availability);
  const metrics = module?.metrics ?? [];
  const byKey = Object.fromEntries(metrics.map((m) => [m.key ?? '', m]));
  const errors = byKey.PIPELINE_ERRORS;
  const banner = healthBannerFromSystemHealth(systemHealth, errors);
  const rows = [
    'REPORT_RATE',
    'CONFIRMED_VIOLATION_RATE',
    'HUMAN_REVIEW_RATE',
    'AVG_TIME_TO_DECISION',
    'AVG_COOP_DEEP_SCAN_MS',
    'PROCESSING_SUCCESS_RATE',
  ]
    .map((key) => byKey[key])
    .filter(Boolean) as ModerationMetricDTO[];

  const errCount = metricValue(errors);
  const vsLabel = comparisonLabel?.replace(/^vs\s+/i, '') ?? '';
  const canShowTrend = showPriorComparison && Boolean(comparisonLabel);

  return (
    <Card className={cn('border-border/40 bg-[#1a1a1c] shadow-none', className)}>
      <CardHeader className="space-y-0 p-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h3 className="text-lg font-semibold leading-6 tracking-tight text-white">
              {module?.label ?? 'Platform Safety Health'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Core safety and moderation health signals
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
            {module?.note ? (
              <span
                className="inline-flex size-7 items-center justify-center rounded-full text-muted-foreground/70"
                title={module.note}
              >
                <Info className="size-4" aria-hidden />
                <span className="sr-only">{module.note}</span>
              </span>
            ) : (
              <span className="inline-flex size-7 items-center justify-center rounded-full text-muted-foreground/50">
                <Info className="size-4" aria-hidden />
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 p-5 pt-0">
        {isLoading ? (
          <DashboardBusy className="h-48" />
        ) : state === 'not-ready' ? (
          <p className="text-sm text-muted-foreground">{module?.note ?? 'Not available yet.'}</p>
        ) : (
          <>
            {/* Figma: tall bordered health banner with circular shield badge */}
            <div
              className={cn(
                'flex items-center gap-3.5 rounded-2xl border px-4 py-4',
                banner.tone === 'ok' && 'border-emerald-500/55 bg-emerald-500/[0.12]',
                banner.tone === 'warn' && 'border-amber-500/50 bg-amber-500/[0.12]',
                banner.tone === 'bad' && 'border-rose-500/50 bg-rose-500/[0.12]',
              )}
            >
              <span
                className={cn(
                  'flex size-11 shrink-0 items-center justify-center rounded-full border',
                  banner.tone === 'ok' && 'border-emerald-400/40 bg-emerald-500/20 text-emerald-400',
                  banner.tone === 'warn' && 'border-amber-400/40 bg-amber-500/20 text-amber-400',
                  banner.tone === 'bad' && 'border-rose-400/40 bg-rose-500/20 text-rose-400',
                )}
              >
                <ShieldCheck className="size-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <p
                  className={cn(
                    'text-xl font-semibold leading-7 tracking-tight',
                    banner.tone === 'ok' && 'text-emerald-400',
                    banner.tone === 'warn' && 'text-amber-300',
                    banner.tone === 'bad' && 'text-rose-300',
                  )}
                >
                  {banner.title}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">{banner.detail}</p>
              </div>
            </div>

            <ul className="divide-y divide-border/20">
              {rows.map((m) => {
                const key = m.key ?? '';
                const meta = ROW_META[key] ?? {
                  icon: CheckCircle2,
                  color: '#94a3b8',
                  chart: 'bars' as const,
                  upIsGood: true,
                  format: (v: number) => formatModCount(v),
                };
                const Icon = meta.icon;
                const val = metricValue(m);
                const delta =
                  canShowTrend && m.deltaPercent != null ? Number(m.deltaPercent) : null;
                const ready = moderationReadyState(m.availability);
                const live = ready !== 'not-ready';
                const sparks = sparklineValues(m);
                const isSuccessRow = key === 'PROCESSING_SUCCESS_RATE';
                const trend = canShowTrend ? formatTrend(key, val, delta, vsLabel) : null;

                return (
                  <li
                    key={key}
                    className="grid grid-cols-[2.75rem_minmax(7.5rem,0.9fr)_minmax(0,1.35fr)_minmax(7rem,0.7fr)] items-center gap-3 py-4 first:pt-2 last:pb-0"
                  >
                    <span
                      className="flex size-11 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${meta.color}26`, color: meta.color }}
                    >
                      <Icon className="size-[18px]" strokeWidth={2} aria-hidden />
                    </span>

                    <div className="min-w-0">
                      <p className="text-[13px] leading-4 text-muted-foreground">{m.label}</p>
                      <p className="mt-1 text-[26px] font-semibold leading-8 tracking-tight tabular-nums text-white">
                        {live && val != null ? meta.format(val) : '—'}
                      </p>
                    </div>

                    <div className="flex min-w-0 items-center justify-center px-1">
                      {sparks.length >= 2 ? (
                        meta.chart === 'bars' ? (
                          <MiniBarSparkline
                            values={sparks}
                            fill={meta.color}
                            className="h-10 w-full max-w-[11rem]"
                          />
                        ) : (
                          <MiniSparkline
                            values={sparks}
                            stroke={meta.color}
                            fill={`${meta.color}33`}
                            showDots
                            className="h-10 w-full max-w-[11rem]"
                          />
                        )
                      ) : (
                        <div className="h-10 w-full max-w-[11rem]" aria-hidden />
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1 text-right">
                      {isSuccessRow ? (
                        <span className="inline-flex items-center gap-2 text-[13px] text-foreground/90">
                          {errCount != null
                            ? `${formatModCount(errCount)} pipeline error${errCount === 1 ? '' : 's'}`
                            : '—'}
                          <span
                            className={cn(
                              'size-2 shrink-0 rounded-full',
                              (errCount ?? 0) === 0
                                ? 'bg-emerald-400'
                                : (errCount ?? 0) < 10
                                  ? 'bg-amber-400'
                                  : 'bg-rose-400',
                            )}
                            aria-hidden
                          />
                        </span>
                      ) : trend != null && live ? (
                        <span
                          className={cn(
                            'text-[13px] font-medium',
                            trendIsFavorable(delta ?? 0, meta.upIsGood)
                              ? 'text-emerald-400'
                              : 'text-rose-400',
                          )}
                        >
                          {trend}
                        </span>
                      ) : (
                        <span className="text-[13px] text-muted-foreground">—</span>
                      )}
                      <NotReadyBadge
                        state={ready}
                        {...(m.note ? { hint: m.note } : {})}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
